import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Divider,
  Flex,
  Heading,
  SimpleGrid,
  Switch,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { MaskedInput } from '../../../../../../components/Form/MaskedInput';
import { ReactSelect } from '../../../../../../components/Form/ReactSelect';
import { IProductBase } from '../../../../../../models/products';
import { showProductsService } from '../../../../../../services/Products/ShowProductsService';
import { updateProductsService } from '../../../../../../services/Products/UpdateProductsService';
import { translateError } from '../../../../../../utils/errors';
import { DefaultLayout } from '../../../../_layout/DefaultLayout';

type UpdateProductFormData = {
  name: string;
  isActive: boolean;
  costCenter: string;
  productCode: string;
  productType: string;
  productNature: string;
  description?: string;
  invoiceType: 'NF' | 'ND';
};

interface ILocationState {
  productId: string;
}

const updateProductFormSchema = Yup.object().shape({
  name: Yup.string().required('Requerido'),
  isActive: Yup.boolean().required('Requerido'),
  costCenter: Yup.string()
    .nullable()
    .required('Requerido')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  productCode: Yup.string()
    .nullable()
    .required('Requerido')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  productType: Yup.string()
    .nullable()
    .required('Requerido')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  productNature: Yup.string()
    .nullable()
    .required('Requerido')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  description: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  invoiceType: Yup.string()
    .oneOf(['NF', 'ND', null], 'Tipo inválido')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .required('Requerido'),
});

export const ProductUpdate = (): JSX.Element => {
  const { goBack } = useHistory();
  const toast = useToast();

  const { state } = useLocation<ILocationState>();

  const [updatingProduct, setUpdatingProduct] = useState<IProductBase>();

  const { control, register, handleSubmit, formState, reset } = useForm({
    resolver: yupResolver(updateProductFormSchema),
  });

  const { errors } = formState;
  const { productId } = state;

  const invoiceTypeSelectOptions = useMemo(
    () => [
      {
        label: 'Nota fiscal',
        value: 'NF',
      },
      {
        label: 'Nota débito',
        value: 'ND',
      },
    ],
    [],
  );

  useEffect(() => {
    async function loadProduct(id: string): Promise<void> {
      try {
        const productData = await showProductsService(id);

        setUpdatingProduct(productData);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar os dados do produto, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    }

    if (productId) {
      loadProduct(productId);
    }
  }, [productId, reset, toast]);

  useEffect(() => {
    if (updatingProduct) {
      reset({
        description: updatingProduct.description,
        name: updatingProduct.name,
        costCenter: updatingProduct.costCenter,
        isActive: updatingProduct.isActive,
        productCode: updatingProduct.productCode,
        productNature: updatingProduct.productNature,
        productType: updatingProduct.productType,
        invoiceType: updatingProduct.invoiceType,
      });
    }
  }, [reset, updatingProduct]);

  const handleUpdateProduct: SubmitHandler<UpdateProductFormData> = useCallback(
    async ({
      description,
      name,
      costCenter,
      isActive,
      productCode,
      productNature,
      productType,
      invoiceType,
    }) => {
      try {
        await updateProductsService({
          productId,
          description,
          name,
          costCenter,
          isActive,
          productCode,
          productNature,
          productType,
          invoiceType,
        });

        toast({
          title: 'Editado com sucesso',
          description: 'O produto foi editado corretamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });

        goBack();
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao editar',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao editar o produto, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [productId, toast, goBack],
  );

  return (
    <DefaultLayout>
      <Box
        as="form"
        flex="1"
        borderRadius={8}
        bg="white"
        p="8"
        onSubmit={handleSubmit(handleUpdateProduct)}
      >
        <Heading size="lg" fontWeight="normal">
          Editar produto
        </Heading>

        <Divider my="6" borderColor="gray.300" />

        <VStack spacing="8" alignItems="flex-start">
          <MaskedInput label="Nome" error={errors.name} {...register('name')} />

          <Box
            width="100%"
            borderWidth={2}
            px="2"
            py="4"
            pb="8"
            borderRadius="8"
          >
            <Heading as="h2" size="md" fontWeight="normal">
              Informações fiscais
            </Heading>

            <SimpleGrid minChildWidth="240px" spacing="6" w="100%" mt="4">
              <MaskedInput
                label="Centro de custo"
                error={errors.costCenter}
                {...register('costCenter')}
              />

              <MaskedInput
                label="Código"
                error={errors.productCode}
                {...register('productCode')}
              />

              <MaskedInput
                label="Tipo"
                error={errors.productType}
                {...register('productType')}
              />

              <MaskedInput
                label="Natureza"
                error={errors.productNature}
                {...register('productNature')}
              />

              <ReactSelect
                label="Tipo de nota"
                name="invoiceType"
                options={invoiceTypeSelectOptions}
                control={control}
                error={errors.invoiceType}
              />
            </SimpleGrid>
          </Box>

          <MaskedInput
            label="Descrição"
            as="textarea"
            minHeight="160px"
            resize="none"
            py="2"
            error={errors.description}
            {...register('description')}
          />

          <Center w="100%">
            <Box>
              <Switch
                label="Ativo"
                error={errors.isActive}
                defaultChecked
                {...register('isActive')}
              />
            </Box>
          </Center>
        </VStack>

        <Flex mt="12" justify="flex-end">
          <ButtonGroup>
            <Button colorScheme="blackAlpha" onClick={goBack}>
              Cancelar
            </Button>

            <Button
              type="submit"
              colorScheme="green"
              isLoading={formState.isSubmitting}
            >
              Salvar
            </Button>
          </ButtonGroup>
        </Flex>
      </Box>
    </DefaultLayout>
  );
};
